import React, { useEffect } from 'react';
import {
  object,
  arrayOf as arrayOfProp,
  shape as shapeProp,
  number as propTypeNumber,
  string as propTypeString,
  func as propTypeFunc,
  bool as propTypeBool
} from 'prop-types';
import {
  params, string, arrayOf, shape, extend, number, bool
} from '@thd-nucleus/data-sources';
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  Skeleton
} from '@one-thd/sui-atomic-components';
import { getBundleQuantity } from '../utils/product-bundle-utils';
import { CustomSkeletonLine } from './subcomponents/CustomSkeletonLine';
import { ProductListContent } from './subcomponents/ProductListContent';
import { ProductListTitle } from './subcomponents/ProductListTitle';
import { AccessoryListContent } from './subcomponents/AccessoryListContent';

/**
 * Props
 *
 * @typedef {object} Props
 * @property {{bundleSpecificationDetails: object}} product Bundle product
 * @property {array} products Array of Products
 *  * @typedef {object} accordionProps
 *  * @property {string} productSpecificationAccordionName Specification Accordion name to
 * open and close from parent accordion group
 *  * @property {number} scrollStickyHeaderHeight Height used to offset the scrolling action
 *  * @property {string} productDetailsAccordionName Product Details Accordion name to open
 * and close from parent accordion group
 */

/**
 * BundleProductList component will be returned, this will show a list of products with image, description and price.
 * @param {Props} Props
 * @returns {React.ReactElement} JSX
 */

const BundleProductList = ({
  product,
  products,
  accessories,
  accordionProps,
  loading,
  loadingAccessories,
  addProduct,
  removeProduct,
  swapProducts,
  swapBack,
  selectableVariantProducts,
  addAccessories,
  variantChangeAlertMessage,
  bundleProductsCount,
  dismissAlert
}) => {
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('list-products.ready');
  }, []);
  const { activeProductsCount } = product?.features ?? {};
  const productsCount = getBundleQuantity(products, activeProductsCount);

  return (
    <div
      data-testid="product-list"
      className="*:sui-border-solid before:sui-border-solid after:sui-border-solid sui-w-full sui-br-1 sui-border-x-1"
    >
      <Accordion defaultExpanded>
        <AccordionHeader aria-controls="accordion1-content" id="accordion-productlist-header">
          {products?.length > 0 ? (
            <ProductListTitle productsCount={productsCount} product={product} />
          ) : (
            <div className="sui-w-full">
              <Skeleton grow disableGutters disablePadding>
                <CustomSkeletonLine height="25px" />
              </Skeleton>
            </div>
          )}
        </AccordionHeader>
        <AccordionBody>
          <ProductListContent
            products={products}
            swapProducts={swapProducts}
            swapBack={swapBack}
            addProduct={addProduct}
            removeProduct={removeProduct}
            loading={loading}
            product={product}
            accordionProps={accordionProps}
            selectableVariantProducts={selectableVariantProducts}
            variantChangeAlertMessage={variantChangeAlertMessage}
            bundleProductsCount={bundleProductsCount}
            dismissAlert={dismissAlert}
          />
        </AccordionBody>
      </Accordion>

      <AccessoryListContent
        accessories={accessories}
        loading={loadingAccessories}
        addAccessories={addAccessories}
      />
    </div>
  );
};

BundleProductList.propTypes = {
  product: shapeProp(),
  products: arrayOfProp(shapeProp({ object })),
  accessories: arrayOfProp(shapeProp()),
  accordionProps: shapeProp({
    productSpecificationAccordionName: propTypeString,
    scrollStickyHeaderHeight: propTypeNumber,
    productDetailsAccordionName: propTypeString
  }),
  addProduct: propTypeFunc.isRequired,
  removeProduct: propTypeFunc.isRequired,
  swapProducts: propTypeFunc.isRequired,
  swapBack: propTypeFunc.isRequired,
  dismissAlert: propTypeFunc.isRequired,
  loading: propTypeBool.isRequired,
  selectableVariantProducts: arrayOfProp(shapeProp({ object })).isRequired,
  loadingAccessories: propTypeBool,
  addAccessories: propTypeFunc.isRequired,
  variantChangeAlertMessage: propTypeString.isRequired,
  bundleProductsCount: propTypeNumber
};

BundleProductList.defaultProps = {
  products: null,
  product: null,
  loadingAccessories: false,
  accessories: null,
  accordionProps: shapeProp({
    productSpecificationAccordionName: null,
    scrollStickyHeaderHeight: 0,
    productDetailsAccordionName: null
  }),
  bundleProductsCount: 0
};

BundleProductList.displayName = 'BundleProductList';

BundleProductList.dataModel = extend(
  {
    products: params({ itemIds: arrayOf(string().isRequired()).isRequired() }).arrayOf({
      itemId: string(),
      media: shape({
        images: arrayOf({
          sizes: arrayOf(string()),
          subType: string(),
          type: string(),
          url: string()
        })
      }),
      identifiers: shape({
        brandName: string(),
        productLabel: string(),
        modelNumber: string(),
        applianceType: string()
      }),
      reviews: shape({
        ratingsReviews: shape({
          averageRating: string(),
          totalReviews: string()
        })
      }),
      pricing: params({ storeId: string() }).shape({
        original: number({ float: true }),
        mapAboveOriginalPrice: bool(),
        promotion: shape({
          type: string(),
          description: shape({
            shortDesc: string(),
            longDesc: string()
          }),
          dollarOff: number({ float: true }),
          percentageOff: number({ float: true }),
          savingsCenter: string(),
          savingsCenterPromos: string(),
          specialBuySavings: string(),
          specialBuyDollarOff: string(),
          specialBuyPercentageOff: string(),
          experienceTag: string(),
          promotionTag: string(),
          subExperienceTag: string()
        }),
        specialBuy: number({ float: true }),
        value: number({ float: true }),
        conditionalPromotions: arrayOf({
          reward: shape({
            tiers: arrayOf({
              minPurchaseAmount: string(),
              rewardAmountPerOrder: string()
            })
          })
        })
      }),
      taxonomy: shape({
        breadCrumbs: arrayOf({
          label: string()
        })
      }),
      keyProductFeatures: params().shape({
        keyProductFeaturesItems: arrayOf(shape({
          features: arrayOf(shape({
            name: string(),
            refinementId: string(),
            refinementUrl: string(),
            value: string()
          }))
        }))
      })
    })
  },
  ProductListContent,
  AccessoryListContent
);

export { BundleProductList };
